'use client';

import { Checkbox } from '@/components/ui/Checkbox';
import { BRAND } from '@/lib/cms/config';
import { Link } from '@/lib/i18n/navigation';
import { NEWSLETTER_BRAND_CONFIG } from '@/lib/newsletter/constants';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import type { FieldError, UseFormRegister } from 'react-hook-form';

import styles from './BookAppointmentModal.module.scss';
import type { AppointmentFormData } from './useAppointmentFormStep';

const brandConfig = NEWSLETTER_BRAND_CONFIG[BRAND];

function EmailLink(chunks: ReactNode) {
  return (
    <a href={`mailto:${brandConfig.privacyEmail}`} className={styles.legalLink}>
      {chunks}
    </a>
  );
}

function PrivacyLink(chunks: ReactNode) {
  return (
    <Link
      href="/privacy-policy"
      className={styles.legalLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      {chunks}
    </Link>
  );
}

function GdprLink(chunks: ReactNode) {
  return (
    <Link href="/gdpr" className={styles.legalLink} target="_blank" rel="noopener noreferrer">
      {chunks}
    </Link>
  );
}

function TermsLink(chunks: ReactNode) {
  return (
    <Link
      href="/terms-of-use"
      className={styles.legalLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      {chunks}
    </Link>
  );
}

function RequiredMark() {
  return (
    <span className={styles.requiredMark} aria-hidden="true">
      *
    </span>
  );
}

function FieldError({ message }: Readonly<{ message?: string }>) {
  if (!message) {
    return null;
  }

  return <span className={styles.fieldError}>{message}</span>;
}

interface AppointmentLegalSectionProps {
  register: UseFormRegister<AppointmentFormData>;
  consentError?: FieldError;
}

/**
 * Marketing opt-in and legal consent copy with rich-text links.
 * Uses a local `useTranslations` call so `t.rich()` is always available.
 */
export function AppointmentLegalSection({
  register,
  consentError,
}: Readonly<AppointmentLegalSectionProps>) {
  const t = useTranslations('appointment.form');
  const brandName = brandConfig.displayName;
  const privacyPolicyLabel = t('privacyPolicyLabel', { brand: brandName });
  const termsLabel = t('termsLabel', { brand: brandName });

  const richLinkValues = {
    brand: brandName,
    privacyEmail: brandConfig.privacyEmail,
    privacyPolicyLabel,
    termsLabel,
    emailLink: EmailLink,
    privacyLink: PrivacyLink,
    gdprLink: GdprLink,
    termsLink: TermsLink,
  };

  return (
    <div className={styles.legalSection}>
      <div className={styles.legalBlock}>
        <Checkbox
          align="top"
          fullWidth
          className={styles.legalCheckbox}
          label={t('marketingConsent')}
          {...register('newsletterOptIn')}
        />
        <p className={styles.legalText}>{t.rich('revokeConsent', richLinkValues)}</p>
        <p className={styles.legalText}>{t.rich('dataProtection', richLinkValues)}</p>
      </div>

      <div className={styles.legalBlock}>
        <Checkbox
          id="appointment-legal-consent"
          align="top"
          fullWidth
          required
          className={styles.legalCheckbox}
          aria-invalid={Boolean(consentError)}
          label={
            <>
              {t.rich('legalConsent', richLinkValues)}
              <RequiredMark />
            </>
          }
          {...register('consent')}
        />
        <FieldError message={consentError?.message} />
      </div>
    </div>
  );
}

AppointmentLegalSection.displayName = 'AppointmentLegalSection';
